import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';

// PrimeNG Imports
import { SharedModule } from 'src/app/private/shared/shared.module';

// Services & Models
import { QRCodeModule } from 'angularx-qrcode';
import { FormBuilderService } from 'src/app/private/services/form-builder.service';
import { 
  DynamicForm, 
  Field, 
  FormFieldConfig, 
  Module,
  AVAILABLE_MODULES,
  UI_TYPE_MAPPINGS,
  applyModuleToForm
} from './models/form-builder.model';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CreateFormComponent } from './create-form/create-form.component';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    QRCodeModule,
    CreateFormComponent,
    NgbDropdownModule,
    // Angular Material — imported directly because SharedModule doesn't re-export them
    MatTableModule,
    MatSortModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatPaginatorModule,
  ],
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss']
})
export class FormBuilderComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Paginator
  totalRows = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Data
  forms: DynamicForm[] = [];
  dataSource: MatTableDataSource<DynamicForm> = new MatTableDataSource<DynamicForm>();
  displayedColumns: string[] = ['sno', 'form_name', 'module', 'status', 'created_at', 'actions'];
  availableModules = AVAILABLE_MODULES;
  uiTypeMappings = UI_TYPE_MAPPINGS;
  
  // Loading States
  loading = false;
  loadingFields = false;
  savingForm = false;
  publishingForm = false;
  deletingForm = false;
  
  // Dialog States
  isEditMode = false;
  publishConfirmation: any = null;
  confirmModalData: any = null;
  
  // Embed Code
  embedCodeForm: DynamicForm | null = null;
  embedCode: string = '';
  qrCodeUrl: string = '';

  // Current Form Being Edited
  currentForm: DynamicForm = this.getEmptyForm();
  
  // Available Fields (Left Panel)
  availableFields: Field[] = [];
  
  // Selected Field for Configuration (Right Panel)
  selectedFieldConfig: FormFieldConfig | null = null;
  
  // Drag & Drop
  draggedField: Field | null = null;
  
  // Active Tab
  activeTab = 0;

  constructor(
    private formBuilderService: FormBuilderService,
    private toastr: ToastrService,
    private router: Router,
    private localStorage: LocalStorageService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadForms();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ Data Loading ============
  
  loadForms(): void {
    this.loading = true;
    let organization_id = this.localStorage.getClientId();
    let campus_id = this.localStorage.getCenterId();
    this.formBuilderService.getDynamicFormList(organization_id, campus_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Transform API response to component format
          let formsList: any[] = [];
          if (Array.isArray(response)) {
            formsList = response;
          } else if (response && Array.isArray(response.data)) {
            formsList = response.data;
          } else if (response && Array.isArray(response.rows)) {
            formsList = response.rows;
          }

          this.forms = formsList.map((form: any) => {
            // If form_field_config exists, map it to fields
            if (form.form_field_config && !form.fields) {
              form.fields = form.form_field_config;
            }
            
            applyModuleToForm(form, this.availableModules);
            return form;
          });
          this.dataSource = new MatTableDataSource<DynamicForm>(this.forms);
          this.totalRows = this.forms.length;
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          this.loading = false;
        },
        error: (error) => {
          this.toastr.error('Failed to load forms');
          this.loading = false;
        }
      });
  }

  loadFieldsByModule(moduleKey: string): void {
    this.loadingFields = true;

    const payload = {
      client_id: this.localStorage.getClientId()
    };

    this.formBuilderService.getFieldsByModule(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          let dynamicFields = response.fields || [];
          let fixedFields = this.fixedColumnsMap[moduleKey] || [];
          // Mark fields with their source type
          const markedFixedFields = fixedFields.map((f : any)=> ({ ...f, _field_source: 'static' }));
          const markedDynamicFields = dynamicFields.map((f : any)=> ({ ...f, _field_source: 'dynamic' }));
          this.availableFields = [...markedFixedFields, ...markedDynamicFields];
          console.log("Fields loaded:", this.availableFields);
          this.loadingFields = false;
        },
        error: (error) => {
          this.toastr.error('Failed to load fields');
          this.loadingFields = false;
        }
      });
  }

  // ============ Form Management ============
  
  openNewFormDialog(content: TemplateRef<any>): void {
    // Reset all state FIRST before opening
    this.resetFormState();
    this.isEditMode = false;
    // Create a completely fresh empty form with a new object reference
    this.currentForm = {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getClientId()
    };
    this.availableFields = [];
    this.selectedFieldConfig = null;
    this.activeTab = 0;

    this.modalService.open(content, {
      size: 'xl', 
      windowClass: 'modal-xl custom-modal-80vw',
      scrollable: true,
      centered: true, 
      backdrop: 'static', 
      keyboard: false
    }).result.then(
      (result) => { this.resetFormState(); },
      (reason) => { this.resetFormState(); }
    );
  }

  resetFormState(): void {
    this.currentForm = {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getClientId()
    };
    this.availableFields = [];
    this.selectedFieldConfig = null;
    this.isEditMode = false;
    this.savingForm = false;
    this.loadingFields = false;
    this.activeTab = 0;
  }

  editForm(form: DynamicForm, content: TemplateRef<any>): void {
    this.isEditMode = true;
    
    // Deep clone and transform API structure to component structure
    this.currentForm = JSON.parse(JSON.stringify(form));
    
    // Transform form_field_config to fields if it exists
    if ((this.currentForm as any).form_field_config) {
      this.currentForm.fields = (this.currentForm as any).form_field_config;
      delete (this.currentForm as any).form_field_config;
    }
    
    // Ensure fields array exists
    if (!this.currentForm.fields) {
      this.currentForm.fields = [];
    }
    
    // Autofill module dropdown (binds to module_name) from stored module_id/module_name
    applyModuleToForm(this.currentForm, this.availableModules);

    const module = this.availableModules.find(
      (m) => m.id === this.currentForm.module_id || m.name === this.currentForm.module_name
    );

    if (module) {
      this.loadFieldsByModule(module.key);
    } else {
      console.warn('Module not found for form:', this.currentForm.module_name || this.currentForm.module_id);
      this.availableFields = [];
    }

    this.modalService.open(content, {
      size: 'xl', 
      windowClass: 'modal-xl custom-modal-80vw',
      scrollable: true,
      centered: true, 
      backdrop: 'static', 
      keyboard: false
    }).result.then(
      (result) => { this.resetFormState(); },
      (reason) => { this.resetFormState(); }
    );
  }

  deleteForm(form: DynamicForm, modalContent: TemplateRef<any>): void {
    if (!form.id) return;
    this.confirmModalData = {
      title: 'Delete Form',
      message: `Are you sure you want to permanently delete the form "${form.form_name}"? This action cannot be undone.`,
      confirmText: 'Delete Form',
      cancelText: 'Cancel',
      type: 'delete',
      icon: 'fa-trash-o',
      colorClass: 'delete',
      form: form
    };
    this.modalService.open(modalContent, { centered: true, size: 'md', windowClass: 'fb-confirm-modal' });
  }

  executeDeleteForm(form: DynamicForm, modal?: any): void {
    if (form.id) {
      this.deletingForm = true;
      const payload = {
        id: form.id
      };
      this.formBuilderService.deleteForm(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastr.success('Form deleted successfully');
            this.deletingForm = false;
            if (modal) {
              modal.close();
            }
            this.loadForms();
          },
          error: (error) => {
            this.toastr.error(error.error?.message || 'Failed to delete form');
            this.deletingForm = false;
          }
        });
    }
  }

  saveForm(modal?: any): void {
    applyModuleToForm(this.currentForm, this.availableModules);

    // The form data is already updated via two-way binding from create-form component
    // Client-side validation
    const validationResult = this.formBuilderService.validateFormConfig(this.currentForm);
    
    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        this.toastr.error(error);
      });
      return;
    }

    this.savingForm = true;
    
    // Preserve the is_published state set by the child component
    if (this.currentForm.is_published === undefined) {
      this.currentForm.is_published = false;
    }
    
    this.currentForm.organization_id = this.localStorage.getClientId();
    this.currentForm.campus_id = this.localStorage.getCenterId();
    
    const request = this.isEditMode && this.currentForm.id
      ? this.formBuilderService.updateForm(this.currentForm)
      : this.formBuilderService.createForm(this.currentForm);

    request.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.toastr.success(`Form saved successfully as ${this.currentForm.is_published ? 'Published' : 'Draft'}`);
          this.savingForm = false;
          if (modal) {
            modal.close('Saved');
          }
          this.loadForms();
        },
        error: (error) => {
          this.toastr.error(error.error?.message || 'Failed to save form');
          this.savingForm = false;
        }
      });
  }

  publishForm(form: DynamicForm, modalContent: TemplateRef<any>): void {
    if (!form.id) return;

    this.confirmModalData = {
      title: 'Publish Form',
      message: `Publishing will make the form "${form.form_name}" publicly accessible. Are you sure you want to continue?`,
      confirmText: 'Publish Now',
      cancelText: 'Cancel',
      type: 'publish',
      icon: 'fa-rocket',
      colorClass: 'publish',
      form: form
    };

    this.modalService.open(modalContent, { centered: true, size: 'md', windowClass: 'fb-confirm-modal' });
  }

  unpublishForm(form: DynamicForm, modalContent: TemplateRef<any>): void {
    if (!form.id) return;

    this.confirmModalData = {
      title: 'Unpublish Form',
      message: `Unpublishing will make the form "${form.form_name}" no longer publicly accessible. Are you sure you want to continue?`,
      confirmText: 'Unpublish',
      cancelText: 'Cancel',
      type: 'unpublish',
      icon: 'fa-eye-slash',
      colorClass: 'unpublish',
      form: form
    };

    this.modalService.open(modalContent, { centered: true, size: 'md', windowClass: 'fb-confirm-modal' });
  }

  onConfirmAction(modal: any): void {
    if (!this.confirmModalData || !this.confirmModalData.form) return;
    const { type, form } = this.confirmModalData;

    if (type === 'publish') {
      this.updateFormStatus(form, true, modal);
    } else if (type === 'unpublish') {
      this.updateFormStatus(form, false, modal);
    } else if (type === 'delete') {
      this.executeDeleteForm(form, modal);
    }
  }

  updateFormStatus(form: DynamicForm, isPublished: boolean, modal?: any): void {
    if (!form.id) return;

    this.publishingForm = true;

    // Create payload with updated status
    const payload = {
      ...form,
      is_published: isPublished

    };

    this.formBuilderService.updateForm(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success(`Form ${isPublished ? 'published' : 'unpublished'} successfully`);
          this.publishingForm = false;
          this.publishConfirmation = null;
          if (modal) {
            modal.close();
          }
          this.loadForms();
        },
        error: (error) => {
          const errorMessage = error.error?.message || (isPublished ? 'Failed to publish form' : 'Failed to unpublish form');
          this.toastr.error(errorMessage);
          this.publishingForm = false;
        }
      });
  }

  // ============ Module Selection ============
  
  onModuleChange(): void {
    applyModuleToForm(this.currentForm, this.availableModules);
    const module = this.availableModules.find(
      (m) => m.id === this.currentForm.module_id || m.name === this.currentForm.module_name
    );
    if (module) {
      this.currentForm.module_id = module.id;
      this.currentForm.module_name = module.name;
      this.currentForm.fields = []; // Clear fields when module changes
      this.loadFieldsByModule(module.key);
      this.activeTab = 1; // Move to form builder tab
    }
  }

  // ============ Drag & Drop ============
  
  onFieldDragStart(field: Field): void {
    this.draggedField = field;
  }

  onFieldDragEnd(): void {
    this.draggedField = null;
  }

  onFieldDrop(): void {
    if (this.draggedField) {
      // Check if field already exists in form
      const exists = this.currentForm.fields.some(
        f => f.field_id === this.draggedField!.id
      );

      if (exists) {
        this.toastr.warning('Field already added to form');
      } else {
        // Create new field config
        const newFieldConfig: FormFieldConfig = {
          field_id: this.draggedField.id,
          field: this.draggedField,
          ui_type: this.getDefaultUIType(this.draggedField.data_type),
          is_required: this.draggedField.is_required || false,
          placeholder: '',
          help_text: '',
          order: this.currentForm.fields.length,
          options: this.draggedField.options || []
        };

        this.currentForm.fields.push(newFieldConfig);
      }

      this.draggedField = null;
    }
  }

  // ============ Field Configuration ============
  
  configureField(fieldConfig: FormFieldConfig): void {
    this.selectedFieldConfig = JSON.parse(JSON.stringify(fieldConfig)); // Deep clone
  }

  saveFieldConfiguration(): void {
    if (this.selectedFieldConfig) {
      const index = this.currentForm.fields.findIndex(
        f => f.field_id === this.selectedFieldConfig!.field_id
      );
      
      if (index !== -1) {
        this.currentForm.fields[index] = this.selectedFieldConfig;
        this.toastr.success('Field configuration saved');
      }
    }
    
    this.selectedFieldConfig = null;
  }

  removeField(fieldConfig: FormFieldConfig): void {
    this.currentForm.fields = this.currentForm.fields.filter(
      f => f.field_id !== fieldConfig.field_id
    );
    
    // Update order
    this.currentForm.fields.forEach((f, index) => {
      f.order = index;
    });
  }

  moveFieldUp(index: number): void {
    if (index > 0) {
      const temp = this.currentForm.fields[index];
      this.currentForm.fields[index] = this.currentForm.fields[index - 1];
      this.currentForm.fields[index - 1] = temp;
      
      // Update order
      this.currentForm.fields.forEach((f, idx) => {
        f.order = idx;
      });
    }
  }

  moveFieldDown(index: number): void {
    if (index < this.currentForm.fields.length - 1) {
      const temp = this.currentForm.fields[index];
      this.currentForm.fields[index] = this.currentForm.fields[index + 1];
      this.currentForm.fields[index + 1] = temp;
      
      // Update order
      this.currentForm.fields.forEach((f, idx) => {
        f.order = idx;
      });
    }
  }

  // ============ UI Type Management ============
  
  getCompatibleUITypes(dataType: string): any[] {
    return this.uiTypeMappings.filter(
      ui => ui.compatibleDataTypes.includes(dataType)
    );
  }

  getDefaultUIType(dataType: string): string {
    const mapping: { [key: string]: string } = {
      'string': 'text',
      'text': 'textarea',
      'number': 'number',
      'boolean': 'checkbox',
      'date': 'date',
      'enum': 'select'
    };
    return mapping[dataType] || 'text';
  }

  onUITypeChange(): void {
    // Reset options if UI type doesn't support them
    if (this.selectedFieldConfig && 
        !['select', 'radio', 'multiselect'].includes(this.selectedFieldConfig.ui_type)) {
      this.selectedFieldConfig.options = [];
    }
  }

  // ============ Public URL ============
  
  copyPublicUrl(form: DynamicForm): void {
    if (form.public_token) {
      const url = this.formBuilderService.getPublicFormUrl(form.public_token);
      navigator.clipboard.writeText(url).then(() => {
        this.toastr.success('Public URL copied to clipboard');
      });
    }
  }

  showQrCode(form: DynamicForm, content: TemplateRef<any>): void {
    if (form.public_token) {
      this.qrCodeUrl = this.formBuilderService.getPublicFormUrl(form.public_token);
      this.modalService.open(content, {
        size: 'sm', 
        centered: true 
      }).result.then(
        () => { this.qrCodeUrl = ''; },
        () => { this.qrCodeUrl = ''; }
      );
    }
  }

  // ============ Embed Code ============
  
  openEmbedCodeDialog(form: DynamicForm, content: TemplateRef<any>): void {
    if (form.public_token) {
      this.embedCodeForm = form;
      this.embedCode = this.generateEmbedCode(form);
      this.modalService.open(content, {
        size: 'lg', 
        centered: true 
      }).result.then(
        () => {
          this.embedCodeForm = null;
          this.embedCode = '';
        },
        () => {
          this.embedCodeForm = null;
          this.embedCode = '';
        }
      );
    }
  }

  generateEmbedCode(form: DynamicForm): string {
    const publicUrl = this.formBuilderService.getPublicFormUrl(form.public_token!);
    return `<iframe src="${publicUrl}" 
        width="100%" 
        height="600" 
        frameborder="0" 
        scrolling="auto"
        style="border: 1px solid #ddd; border-radius: 8px;">
</iframe>`;
  }

  copyEmbedCode(): void {
    navigator.clipboard.writeText(this.embedCode).then(() => {
      this.toastr.success('Embed code copied to clipboard');
    });
  }

  // ============ Helpers ============
  
  getEmptyForm(): DynamicForm {
    return {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getClientId()
    };
  }

  getModuleName(moduleId: string): string {
    const module = this.availableModules.find(m => m.id === moduleId);
    return module ? module.name : 'Unknown';
  }

  showOptions(fieldConfig: FormFieldConfig): boolean {
    return ['select', 'radio', 'multiselect'].includes(fieldConfig.ui_type);
  }


  fixedColumnsMap: any = {
    lead: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'query_type', label: 'Query Type' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'interested_course', label: 'Interested Course' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'source', label: 'Source' },
      { field: 'lead_status', label: 'Lead Status' }
    ],
    registration: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'status', label: 'Status' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'preferred_course', label: 'Preferred Course' },
      { field: 'document_status', label: 'Document Status' },
      { field: 'source', label: 'Source' }
    ],
    application: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'preferred_course', label: 'Preferred Course' },
      { field: 'source', label: 'Source' },
      { field: 'application_status', label: 'Application Status' },
      { field: 'document_status', label: 'Document Status' }
    ],
    enrollment: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'address', label: 'Address' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' }
    ],
    admission: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'blood_group', label: 'Blood Group' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'address', label: 'Address' }
    ],
    student: [
      { field: 'admission_number', label: 'Admission Number' },
      { field: 'student_roll_no', label: 'Student Roll No' },
      { field: 'admission_id', label: 'Admission ID' },
      { field: 'academic_year', label: 'Academic Year' },
      { field: 'admission_type', label: 'Admission Type' },
      { field: 'admission_mode', label: 'Admission Mode' },
      { field: 'first_name', label: 'First Name' },
      { field: 'middle_name', label: 'Middle Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'gender', label: 'Gender' },
      { field: 'dob', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'religion', label: 'Religion' },
      { field: 'caste', label: 'Caste' },
      { field: 'caste_category', label: 'Caste Category' },
      { field: 'aadhar_no', label: 'Aadhar Number' },
      { field: 'passport_no', label: 'Passport Number' },
      { field: 'email', label: 'Email' },
      { field: 'alternate_email', label: 'Alternate Email' },
      { field: 'phone', label: 'Phone' },
      { field: 'alternate_phone', label: 'Alternate Phone' },
      { field: 'address_line1', label: 'Address Line 1' },
      { field: 'address_line2', label: 'Address Line 2' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'country', label: 'Country' },
      { field: 'permanent_address', label: 'Permanent Address' },
      { field: 'current_address', label: 'Current Address' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_relation', label: 'Guardian Relation' },
      { field: 'guardian_phone', label: 'Guardian Phone' },
      { field: 'guardian_email', label: 'Guardian Email' },
      { field: 'guardian_address', label: 'Guardian Address' },
      { field: 'father_name', label: 'Father Name' },
      { field: 'father_phone', label: 'Father Phone' },
      { field: 'father_email', label: 'Father Email' },
      { field: 'father_occupation', label: 'Father Occupation' },
      { field: 'mother_name', label: 'Mother Name' },
      { field: 'mother_phone', label: 'Mother Phone' },
      { field: 'mother_email', label: 'Mother Email' },
      { field: 'mother_occupation', label: 'Mother Occupation' },
      { field: 'annual_income', label: 'Annual Income' },
      { field: 'previous_school', label: 'Previous School' },
      { field: 'previous_class', label: 'Previous Class' },
      { field: 'previous_percentage', label: 'Previous Percentage' },
      { field: 'transfer_certificate', label: 'Transfer Certificate' },
      { field: 'achievements', label: 'Achievements' },
      { field: 'course_id', label: 'Course' },
      { field: 'department_id', label: 'Department' }
    ]
  };
}
